import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { cn } from "~/lib/utils";
import { Markdown } from "./Markdown";
import type { TrailEntry } from "./useTurns";

interface ThinkingTrailProps {
  // The turn's thinking, in emission order. Empty renders nothing.
  entries: TrailEntry[];
}

// A settled turn's thinking: everything the model narrated between steps, in
// one collapsed row above the answer.
//
// Rendered only once the turn has finished — the trail is a record to read
// afterwards, not a feed to watch, and a live one would be the same moving
// target the narration already was. It starts closed on every mount, including
// after a reload, so a transcript of settled turns stays as quiet as it is now
// and discloses its thinking only where the reader asks for it.
//
// Separate from the tool-call disclosure by design: that row keeps its own
// contents and lifecycle untouched.
export const ThinkingTrail = ({ entries }: ThinkingTrailProps) => {
  const [open, setOpen] = useState(false);

  if (entries.length === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="w-full">
      <CollapsibleTrigger
        className={cn(
          "group inline-flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground",
          "hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        )}
      >
        <ChevronRight
          className="size-3.5 transition-transform group-data-[state=open]:rotate-90"
          aria-hidden
        />
        <span>Reasoning</span>
      </CollapsibleTrigger>
      <CollapsibleContent animated>
        <div className="mt-1 flex flex-col gap-2 border-l border-border pl-3">
          {entries.map((entry) => (
            <div key={entry.id} className="text-xs leading-relaxed text-muted-foreground">
              <Markdown text={entry.text} />
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
